import { Heart } from "lucide-react";
import { BODY_TYPES, PHASES } from "@/lib/lunelle-data";
import { useAccount } from "@/lib/account";
import { mealFor, whyFor } from "@/lib/faith-customs";
import { RecipeCard } from "./RecipeCard";

const PH = Object.fromEntries(PHASES.map((p) => [p.id, p]));
const BODY = Object.fromEntries(BODY_TYPES.map((b) => [b.id, b.name]));

// Week / Saved switch above the kitchen list
export const KitchenViewSwitch = () => {
  const { user, kitchenView, setKitchenView } = useAccount();
  const count = Array.isArray(user?.favorites) ? user.favorites.length : 0;
  const tab = (id, label, testId) => (
    <button type="button" onClick={() => setKitchenView(id)} data-testid={testId} aria-pressed={kitchenView === id}
      className={`inline-flex items-center gap-1.5 border-b-2 pb-2 text-sm font-semibold transition-colors ${kitchenView === id ? "border-[#9E4429] text-[#2C221E]" : "border-transparent text-[#6B5A52] hover:text-[#2C221E]"}`}>
      {label}
    </button>
  );
  return (
    <div className="mb-6 flex gap-6" role="group" aria-label="Kitchen view">
      {tab("week", "This week's plates", "kitchen-view-week")}
      {tab("saved", <><Heart className="h-3.5 w-3.5" />Saved{user ? ` (${count})` : ""}</>, "kitchen-view-saved")}
    </div>
  );
};

// Member's hearted recipes, newest first, in her faith's version
export const SavedRecipes = ({ faith }) => {
  const { user, openAuth } = useAccount();
  const ids = (Array.isArray(user?.favorites) ? user.favorites : []).filter((id) => PH[id.split("-")[0]]).slice().reverse();
  if (!user) {
    return (
      <div className="rounded-3xl border border-dashed border-[#C86D51]/35 bg-[#FDFBF7]/70 p-8" data-testid="saved-recipes-signed-out">
        <p className="font-serif text-2xl text-[#2C221E]">Keep the plates you love.</p>
        <p className="mt-2 text-sm leading-relaxed text-[#5C4A42]">Sign in, tap the heart on any recipe, and it waits for you here on every device.</p>
        <button type="button" onClick={() => openAuth("signin")} data-testid="saved-recipes-sign-in" className="mt-5 rounded-full bg-[#2C221E] px-5 py-3 text-sm font-semibold text-[#FBF5EE] transition-colors hover:bg-[#9E4429]">Sign in to save recipes</button>
      </div>
    );
  }
  if (!ids.length) {
    return (
      <div className="rounded-3xl border border-dashed border-[#C86D51]/35 bg-[#FDFBF7]/70 p-8" data-testid="saved-recipes-empty">
        <p className="font-serif text-2xl text-[#2C221E]">No saved recipes yet.</p>
        <p className="mt-2 text-sm leading-relaxed text-[#5C4A42]">Tap the <Heart className="inline h-3.5 w-3.5 text-[#9E4429]" /> next to any recipe in this week's plates to keep it here.</p>
      </div>
    );
  }
  return (
    <ol className="divide-y divide-[#C86D51]/20 border-y border-[#C86D51]/20" data-testid="saved-recipes-list">
      {ids.map((id) => {
        const [phase, body] = id.split("-");
        const p = PH[phase];
        return (
          <li key={id} className="py-6" data-testid={`saved-recipe-${id}`}>
            <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: p.color }}>{p.name} · {BODY[body]}</p>
            <p className="mt-1.5 text-[#3D302B]">{mealFor(phase, body, faith)}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-[#6B5A52]"><strong className="font-semibold text-[#9E4429]">Why it helps:</strong> {whyFor(phase, body)}</p>
            <RecipeCard phase={phase} body={body} faith={faith} phaseName={p.name} testId={`saved-recipe-open-${id}`} />
          </li>
        );
      })}
    </ol>
  );
};
